import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router';
import {
  Receipt,
  Download,
  ArrowLeft,
  Loader2,
  Mail,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Button,
  StatusBadge,
  NotificationCard,
  MoneyCell,
  DateCell,
  Card,
  EftBankDetailsCard,
  PayNowButton,
  InvoiceDocument,
  pdf,
  useToast,
  type StatusTone,
  type InvoicePDFCompany,
  type InvoicePDFCustomer,
  type InvoicePDFInvoice,
  type InvoicePDFLineItem,
} from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { useInvoice, usePayFastInitiate } from '@/domains/billing';
import { useCurrentCustomer } from '@/domains/customers';
import { relativeExpiry, formatZAR } from '@/shared/utils/format';
import { fireAdsPurchase } from '@/shared/lib/ads';
import { PaymentTrustStrip } from '@/shared/components/feedback/PaymentTrustStrip';
import type { InvoiceStatus, Invoice } from '@/domains/billing';

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  paid: 'Paid',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const STATUS_TONE: Record<InvoiceStatus, StatusTone> = {
  draft: 'draft',
  unpaid: 'pending',
  overdue: 'overdue',
  paid: 'paid',
  cancelled: 'draft',
  refunded: 'info',
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const { data: invoice, isLoading, error, refetch } = useInvoice(id);
  const { data: customer } = useCurrentCustomer();
  const [downloading, setDownloading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const {
    initiate: initiatePayFast,
    isLoading: payFastLoading,
    error: payFastError,
  } = usePayFastInitiate(id ?? '');

  // Handle PayFast return URLs. Per Phase 9 review R1, the query param is a
  // HINT only — the success/failure UI state is derived from the refetched
  // `invoice.lifecycle_state`, never from the URL. Param is also stripped
  // from the URL after handling so a refresh doesn't re-fire the toast.
  useEffect(() => {
    const paid = searchParams.get('paid');
    const cancelled = searchParams.get('cancelled');
    if (!paid && !cancelled) return;

    if (paid === '1') {
      toast({
        tone: 'info',
        title: 'Confirming payment…',
        body: 'PayFast just notified us. Refreshing your invoice.',
      });
      refetch();
    } else if (cancelled === '1') {
      toast({
        tone: 'warning',
        title: 'Payment cancelled',
        body: 'Your invoice is still unpaid. You can try again or pay by EFT.',
      });
    }

    // Strip the param so a page refresh doesn't re-fire the toast
    const next = new URLSearchParams(searchParams);
    next.delete('paid');
    next.delete('cancelled');
    setSearchParams(next, { replace: true });
  }, [searchParams, refetch, setSearchParams, toast]);

  // Surface PayFast initiate errors as a toast (network failure, 4xx, etc.)
  useEffect(() => {
    if (payFastError) {
      toast({
        tone: 'danger',
        title: 'Could not start payment',
        body: payFastError.message,
      });
    }
  }, [payFastError, toast]);

  // Fire the Google Ads purchase conversion when this invoice is confirmed
  // paid. Secondary path (existing customers); the primary ad path is the
  // public PayInvoicePage. fireAdsPurchase gates on marketing consent and
  // dedupes on invoice.number, so re-viewing an old paid invoice never
  // double-counts. See CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md (AD-1).
  useEffect(() => {
    if (invoice && invoice.status === 'paid') {
      fireAdsPurchase({
        invoiceId: invoice.id,
        value: invoice.totalZAR,
        number: invoice.number,
      });
    }
  }, [invoice]);

  if (isLoading) {
    return (
      <UniversalPage
        title="Invoice"
        breadcrumbs={[
          { label: 'Home', href: '/app' },
          { label: 'Billing', href: '/app/billing' },
          { label: 'Invoices', href: '/app/billing' },
          { label: id ?? 'Detail' },
        ]}
      >
        <UniversalLoadingState variant="spinner" caption="Loading invoice…" />
      </UniversalPage>
    );
  }

  if (error) {
    return (
      <UniversalPage title="Could not load invoice">
        <NotificationCard tone="danger" title="Could not load invoice" body={error.message} />
      </UniversalPage>
    );
  }

  if (!invoice) {
    return (
      <UniversalPage title="Invoice not found">
        <Card interactive={false} className="p-12 text-center text-sm text-muted-foreground">
          We could not find that invoice on your account.{' '}
          <Link to="/app/billing" className="text-primary hover:underline">
            Back to invoices
          </Link>
        </Card>
      </UniversalPage>
    );
  }

  async function downloadPdf() {
    if (!invoice || !customer) return;
    setDownloading(true);
    try {
      const blob = await pdf(buildInvoiceDocument(invoice, customer)).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed', err);
    } finally {
      setDownloading(false);
    }
  }

  const isUnpaid = invoice.status === 'unpaid' || invoice.status === 'overdue';

  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          <span className="tabular-nums">{invoice.number}</span>
        </span>
      }
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Invoices', href: '/app/billing' },
        { label: invoice.number },
      ]}
      meta={
          <>
            {invoice.docType === 'quotation' && (
              <StatusBadge tone="expiring">Quotation</StatusBadge>
            )}
            <StatusBadge tone={STATUS_TONE[invoice.status]}>
              {STATUS_LABEL[invoice.status]}
            </StatusBadge>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1">
              Issued <DateCell value={invoice.issuedAt} format="long" />
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1">
              {invoice.status === 'paid' && invoice.paidAt ? (
                <>Paid <DateCell value={invoice.paidAt} format="long" /></>
              ) : (
                <>Due <DateCell value={invoice.dueAt} format="long" /> ({relativeExpiry(invoice.dueAt)})</>
              )}
            </span>
          </>
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              disabled={downloading || !customer}
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download PDF
            </Button>
            {isUnpaid && (
              <PayNowButton
                size="sm"
                amountDisplay={formatZAR(invoice.totalZAR)}
                onClick={initiatePayFast}
                isLoading={payFastLoading}
              />
            )}
          </>
        }
    >
      {invoice.docType === 'quotation' && isUnpaid && (
        <NotificationCard
          tone="info"
          title="This is a quotation — pay to accept it."
          body={
            <span>
              Paying {formatZAR(invoice.totalZAR)} accepts this quotation and
              converts it to an invoice; your service activates automatically.
              {invoice.quoteExpiresAt && (
                <> Valid until <DateCell value={invoice.quoteExpiresAt} format="long" />.</>
              )}
            </span>
          }
        />
      )}

      {invoice.status === 'paid' && invoice.quoteNumber && (
        <p className="text-xs text-muted-foreground">
          Converted from quotation {invoice.quoteNumber}.
        </p>
      )}

      {invoice.status === 'paid' && invoice.paidAt && (
        <NotificationCard
          tone="success"
          title="Payment received — thank you."
          body={
            <span className="inline-flex items-center gap-1">
              <MoneyCell value={invoice.totalZAR} /> settled on <DateCell value={invoice.paidAt} format="long" />.
              A receipt was emailed to your billing address.
            </span>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SectionCard title="Line items">
            {/* Six columns of invoice detail do not fit a phone. Without this
                the table forces the whole page to scroll sideways, so the nav
                and every other card drift off-screen with it. */}
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Unit price</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoice.lines.map((line) => {
                  // An item included free: discounted to nothing, with a reason
                  // recorded. Show what it is WORTH struck through — a bare
                  // R0.00 tells the customer nothing about what they received.
                  const included = (line.discountZAR ?? 0) > 0 && line.amountZAR === 0;
                  return (
                    <tr key={line.id}>
                      <td className="px-4 py-3 text-navy-primary">
                        {line.description}
                        {included && (
                          <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                            Included
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-foreground-soft">
                        {line.quantity}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {included ? (
                          <span className="text-foreground-soft line-through">
                            <MoneyCell value={line.unitPriceZAR} />
                          </span>
                        ) : (
                          <MoneyCell value={line.unitPriceZAR} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {included ? (
                          <span className="font-semibold text-primary">FREE</span>
                        ) : (
                          <MoneyCell value={line.amountZAR} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-border text-sm">
                {siteConfig.vatRegistered ? (
                  <>
                    <tr>
                      <td colSpan={3} className="px-4 py-2 text-right text-muted-foreground">
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right">
                        <MoneyCell value={invoice.subtotalZAR} />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-4 py-2 text-right text-muted-foreground">
                        VAT
                      </td>
                      <td className="px-4 py-2 text-right">
                        <MoneyCell value={invoice.vatZAR} />
                      </td>
                    </tr>
                  </>
                ) : null}
                <tr className="border-t border-border">
                  <td colSpan={3} className="px-4 py-3 text-right font-semibold text-navy-primary">
                    Total due
                  </td>
                  <td className="px-4 py-3 text-right">
                    <MoneyCell value={invoice.totalZAR} className="text-base" />
                  </td>
                </tr>
              </tfoot>
            </table>
            </div>
          </SectionCard>

          {invoice.quoteMetadata?.kind === 'studio_deposit' && (
            <SectionCard title="Studio Starter payment plan">
              <StudioPlanBreakdown meta={invoice.quoteMetadata} />
            </SectionCard>
          )}

          {!siteConfig.vatRegistered && (
            <p className="text-xs text-muted-foreground">
              {siteConfig.legalName} is not registered for VAT.
              This document is not a tax invoice.
            </p>
          )}
        </div>

        <div className="space-y-6">
          {isUnpaid && (
            <EftBankDetailsCard
              invoiceNumber={invoice.number}
              amountDisplay={formatZAR(invoice.totalZAR)}
              bankName={siteConfig.bank.name}
              accountName={siteConfig.bank.accountName}
              accountNumber={siteConfig.bank.accountNumber}
              branchCode={siteConfig.bank.branchCode}
            />
          )}

          {isUnpaid && <PaymentTrustStrip compact />}

          <SectionCard title="Bill to">
            {customer ? (
              <div className="space-y-1 text-sm">
                <p className="font-medium text-navy-primary">
                  {customer.companyName ?? customer.fullName}
                </p>
                {customer.companyName && (
                  <p className="text-foreground-soft">Att: {customer.fullName}</p>
                )}
                <p className="text-foreground-soft">{customer.email}</p>
                {customer.contactNumber && (
                  <p className="text-foreground-soft">{customer.contactNumber}</p>
                )}
                {customer.billingAddress && (
                  <div className="mt-2 text-foreground-soft">
                    <p>{customer.billingAddress.line1}</p>
                    {customer.billingAddress.line2 && (
                      <p>{customer.billingAddress.line2}</p>
                    )}
                    <p>
                      {customer.billingAddress.city},{' '}
                      {customer.billingAddress.postalCode}
                    </p>
                    <p>{customer.billingAddress.countryCode}</p>
                  </div>
                )}
                {customer.vatNumber && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Customer VAT: {customer.vatNumber}
                  </p>
                )}
                <Link
                  to="/app/settings/billing-info"
                  className="mt-3 inline-flex text-xs text-primary hover:underline"
                >
                  Edit billing details
                </Link>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}
          </SectionCard>

          <SectionCard title="Need help?">
            <p className="text-sm text-foreground-soft">
              Question about this invoice? Email us — we reply within one
              business day.
            </p>
            <a
              href={`mailto:${siteConfig.supportEmail}?subject=${encodeURIComponent(
                `Invoice ${invoice.number}`
              )}`}
              className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Mail className="h-4 w-4" />
              {siteConfig.supportEmail}
            </a>
            {invoice.status === 'paid' && (
              <div className="mt-3 border-t border-border pt-3">
                <Link
                  to={`/app/support/new?dept=billing&subject=${encodeURIComponent(
                    `Refund request — ${invoice.number}`
                  )}&body=${encodeURIComponent(
                    `Hi, I'd like to request a refund for invoice ${invoice.number} (${formatZAR(invoice.totalZAR)}).\n\nReason:\n`
                  )}`}
                  className="text-sm text-primary hover:underline"
                >
                  Request a refund for this invoice
                </Link>
                <p className="mt-1 text-xs text-muted-foreground">
                  Reviewed per our refund policy — see sighthost.online/refund.
                </p>
              </div>
            )}
          </SectionCard>

          <Link
            to="/app/billing"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-navy-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            All invoices
          </Link>
        </div>
      </div>
    </UniversalPage>
  );
}

function StudioPlanBreakdown({ meta }: { meta: NonNullable<Invoice['quoteMetadata']> }) {
  const zar = (c?: number) => formatZAR((c ?? 0) / 100);
  return (
    <div className="space-y-1 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">SightHost Studio Starter</span>
        <span>{zar(meta.total_cents)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Less: deposit payable now</span>
        <span>({zar(meta.deposit_cents)})</span>
      </div>
      <div className="flex justify-between border-t border-border pt-1">
        <span className="text-muted-foreground">Balance on payment plan</span>
        <span className="font-medium">{zar(meta.balance_cents)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Payment plan</span>
        <span className="font-semibold text-navy-primary">
          {meta.plan_months} × {zar(meta.installment_cents)}/month
        </span>
      </div>
      {(meta.rounding_discount_cents ?? 0) > 0 && (
        <p className="pt-1 text-xs text-muted-foreground">
          Includes a {zar(meta.rounding_discount_cents)} rounding discount — you
          never pay more than {zar(meta.total_cents)} in total.
        </p>
      )}
      {(meta.included_value_zar ?? 0) > 0 && (
        <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <p className="text-sm font-semibold text-navy-primary">
            {formatZAR(meta.included_value_zar ?? 0)} of extras, included free
          </p>
          {meta.included_items && meta.included_items.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {meta.included_items.map((item) => (
                <li key={item.label} className="flex justify-between gap-4">
                  <span>{item.label}</span>
                  <span className="tabular-nums line-through">
                    {formatZAR(item.list_zar)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1.5 text-xs text-muted-foreground">
            Already part of your package — there is nothing more to pay for these.
          </p>
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card interactive={false}>
      <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-navy-primary">
        {title}
      </h3>
      <div className="px-6 py-5">{children}</div>
    </Card>
  );
}

/** Build the @react-pdf Document props from portal types. */
function buildInvoiceDocument(
  invoice: Invoice,
  customer: ReturnType<typeof useCurrentCustomer>['data'] extends infer C ? C : never
) {
  if (!customer) {
    throw new Error('No customer profile available for invoice render.');
  }

  const company: InvoicePDFCompany = {
    companyName: siteConfig.name,
    legalName: siteConfig.legalName,
    address: `${siteConfig.address.street}, ${siteConfig.address.locality}`,
    city: siteConfig.address.city,
    postalCode: siteConfig.address.postalCode,
    phone: siteConfig.phoneDisplay,
    email: siteConfig.supportEmail,
    website: siteConfig.url,
    registrationNumber: siteConfig.registration,
    vatNumber: siteConfig.vatNumber || undefined,
    vatRegistered: siteConfig.vatRegistered,
  };

  const customerForPdf: InvoicePDFCustomer = {
    name: customer.companyName ?? customer.fullName,
    contactPerson: customer.companyName ? customer.fullName : undefined,
    address: customer.billingAddress?.line1,
    city: customer.billingAddress?.city,
    postalCode: customer.billingAddress?.postalCode,
    email: customer.email,
    phone: customer.contactNumber,
    vatNumber: customer.vatNumber,
  };

  const isQuotation = invoice.docType === 'quotation';
  const invoiceForPdf: InvoicePDFInvoice = {
    number: invoice.number,
    issueDate: invoice.issuedAt,
    dueDate: invoice.dueAt,
    paymentTerms: isQuotation ? 'Pay to accept' : 'Net 30 days',
    subtotal: invoice.subtotalZAR,
    vatAmount: invoice.vatZAR,
    total: invoice.totalZAR,
    statusLabel:
      invoice.status === 'paid'
        ? 'PAID'
        : invoice.status === 'overdue'
          ? 'OVERDUE'
          : undefined,
    documentTitle: isQuotation ? 'QUOTATION' : undefined,
    documentSubtitle: isQuotation
      ? invoice.quoteExpiresAt
        ? `Valid until ${new Date(invoice.quoteExpiresAt).toLocaleDateString('en-ZA')}`
        : undefined
      : invoice.quoteNumber
        ? `Ref: quotation ${invoice.quoteNumber}`
        : undefined,
    footerNote: isQuotation
      ? 'This is a quotation, not a tax invoice and not a demand for payment. Paying the amount accepts this quotation.'
      : undefined,
    notes:
      invoice.quoteMetadata?.kind === 'studio_deposit'
        ? [
            `SightHost Studio Starter total: ${formatZAR((invoice.quoteMetadata.total_cents ?? 0) / 100)}`,
            `Less: deposit payable now: (${formatZAR((invoice.quoteMetadata.deposit_cents ?? 0) / 100)})`,
            `Balance on payment plan: ${formatZAR((invoice.quoteMetadata.balance_cents ?? 0) / 100)}`,
            `Payment plan: ${invoice.quoteMetadata.plan_months} monthly instalments of ${formatZAR((invoice.quoteMetadata.installment_cents ?? 0) / 100)}`,
            ...((invoice.quoteMetadata.rounding_discount_cents ?? 0) > 0
              ? [`Includes a ${formatZAR((invoice.quoteMetadata.rounding_discount_cents ?? 0) / 100)} rounding discount.`]
              : []),
          ].join('\n')
        : undefined,
  };

  const lineItems: InvoicePDFLineItem[] = invoice.lines.map((l) => ({
    description: l.description,
    quantity: l.quantity,
    unitPrice: l.unitPriceZAR,
    vatRate: l.vatRate,
  }));

  // One-click PayFast checkout link — only rendered on unpaid invoices that
  // still carry their bearer token. Mirrors the same /pay/:id?token=… URL the
  // invoice email uses, so a customer or their bookkeeper can pay from the PDF
  // without signing into the portal.
  const showPayNow =
    (invoice.status === 'unpaid' || invoice.status === 'overdue') &&
    !!invoice.paymentToken;
  const payNow = showPayNow
    ? {
        url: `${siteConfig.portal.productionUrl}/pay/${invoice.id}?token=${invoice.paymentToken}`,
        amountDisplay: formatZAR(invoice.totalZAR),
      }
    : undefined;

  return (
    <InvoiceDocument
      company={company}
      customer={customerForPdf}
      invoice={invoiceForPdf}
      lineItems={lineItems}
      payNow={payNow}
    />
  );
}
